/**
 * MUI X's date picker, drawn to Figma's `↪ Date picker` page (`23026:177`). Only `ui/date-picker`
 * renders these components, so every entry here is that primitive's look.
 *
 * The field is not an `OutlinedInput` — MUI X ships its own `PickersOutlinedInput` with a sectioned
 * `contentEditable` DOM — so the `MuiOutlinedInput` treatment is applied to it again through
 * `outlinedControlRoot` and the field geometry rather than inherited.
 */

import type {} from "@mui/x-date-pickers/themeAugmentation";

import {
  CONTROL_PADDING,
  type FieldControlSize,
  pickersInputBase,
  pickersInputSizeStyle,
} from "theme/components/field-geometry";
import { outlinedControlRoot } from "theme/components/forms";
import { type ComponentOverrides } from "theme/theme.types";

import { borderRadius, shadows } from "design-tokens";

/** Figma's day cell is 36px with an 8px column gap — MUI's default margin is 2px a side. */
const DAY_HORIZONTAL_MARGIN = "4px";

export const MuiPickersOutlinedInput: ComponentOverrides["MuiPickersOutlinedInput"] = {
  styleOverrides: {
    // Keyed on `inputSize`, the name MUI X gives the text field's `size` on the input's owner state.
    root: ({ theme }) => ({
      ...outlinedControlRoot(theme, ".MuiPickersOutlinedInput-notchedOutline"),
      ...pickersInputBase(theme),
      variants: (Object.keys(CONTROL_PADDING) as FieldControlSize[]).map((size) => ({
        props: { inputSize: size },
        style: pickersInputSizeStyle(theme, size),
      })),
    }),
    /**
     * The empty field shows the format as its placeholder, and MUI dims it with `opacity` rather than
     * a colour — so it would not match `ui/text-field`'s placeholder. A disabled value keeps its body
     * colour, as on `MuiOutlinedInput`; MUI paints `action.disabled` on the root.
     */
    sectionsContainer: ({ theme, ownerState }) => ({
      ...(ownerState.isFieldValueEmpty &&
        !ownerState.isFieldFocused && { opacity: 1, color: theme.palette.text.disabled }),
      ".Mui-disabled > &": { color: theme.palette.text.primary },
    }),
  },
};

export const MuiPickerPopper: ComponentOverrides["MuiPickerPopper"] = {
  styleOverrides: {
    paper: ({ theme }) => ({
      marginTop: theme.spacing(1),
      border: `1px solid ${theme.palette.border.secondary}`,
      borderRadius: borderRadius.DEFAULT,
      // Figma's `shadowMedium`. The Popper paper sits on MUI's rung 8, which is `sm`.
      boxShadow: shadows.md,
    }),
  },
};

export const MuiDateCalendar: ComponentOverrides["MuiDateCalendar"] = {
  styleOverrides: {
    /**
     * Figma's view caret points right under the day grid and down, in grey, under the year list. MUI
     * turns it with a `view` prop that no theme callback receives, so the year state is read off the
     * DOM instead — `:has()` also carries the extra class needed to beat MUI's own year-view rotation.
     */
    root: ({ theme }) => ({
      "& .MuiPickersCalendarHeader-switchViewIcon": {
        transform: "rotate(-90deg)",
        color: theme.palette.primary.main,
      },
      "&:has(.MuiYearCalendar-root) .MuiPickersCalendarHeader-switchViewIcon": {
        transform: "rotate(0deg)",
        color: theme.palette.text.secondary,
      },
    }),
  },
};

export const MuiPickersCalendarHeader: ComponentOverrides["MuiPickersCalendarHeader"] = {
  styleOverrides: {
    // The root is a fixed 40px row, so these margins centre the label where Figma does, 25px below the
    // paper's top edge. The weekday row lands 40px below it against Figma's 38; closing that means
    // shrinking MUI's 40px weekday row, which also spaces the grid.
    root: ({ theme }) => ({
      marginTop: theme.spacing(0.5),
      marginBottom: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    }),
    labelContainer: ({ theme }) => ({ ...theme.typography.subtitle1 }),
  },
};

export const MuiDayCalendar: ComponentOverrides["MuiDayCalendar"] = {
  styleOverrides: {
    weekDayLabel: ({ theme }) => ({
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      marginLeft: DAY_HORIZONTAL_MARGIN,
      marginRight: DAY_HORIZONTAL_MARGIN,
    }),
  },
};

export const MuiPickerDay: ComponentOverrides["MuiPickerDay"] = {
  defaultProps: { disableRipple: true },
  styleOverrides: {
    /**
     * One slot, so every state is a `root` descendant key and wins on order against MUI's own
     * `variants`. Borders are `outline` with a negative offset, as MUI draws `today`, so no state
     * shifts the grid; the selected ring is the field's focus ring.
     */
    root: ({ theme }) => ({
      "--PickerDay-horizontalMargin": DAY_HORIZONTAL_MARGIN,
      ...theme.typography.body2,
      borderRadius: borderRadius.md,
      // The calendar opens with a day already focused (its roving tab stop), so MUI's `:focus` tint
      // would paint today on every open; Figma draws it white. Keyboard focus keeps the tint.
      "&:focus": { backgroundColor: "transparent" },
      "&:hover": { backgroundColor: theme.palette.primary.lighter },
      "&:focus-visible": { backgroundColor: theme.palette.primary.lighter },
      "&.MuiPickerDay-today:not(.Mui-selected)": {
        outline: `1px solid ${theme.palette.grey[400]}`,
        outlineOffset: -1,
      },
      "&.Mui-selected": {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.body2.fontWeight,
        backgroundColor: theme.palette.primary.lighter,
        outline: `1px solid ${theme.palette.primary.main}`,
        outlineOffset: -1,
        boxShadow: shadows.input,
      },
      "&.Mui-selected:hover": { backgroundColor: theme.palette.primary.lighter },
      "&.Mui-selected:focus": { backgroundColor: theme.palette.primary.lighter },
      "&.Mui-selected.Mui-disabled": {
        opacity: 1,
        color: theme.palette.text.disabled,
        backgroundColor: theme.palette.grey[100],
        outlineColor: theme.palette.border.secondary,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: theme.palette.primary.lighter,
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
      },
    }),
  },
};

export const MuiYearCalendar: ComponentOverrides["MuiYearCalendar"] = {
  styleOverrides: {
    button: ({ theme }) => ({
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      borderRadius: borderRadius.md,
      "&:focus": { backgroundColor: "transparent" },
      "&:hover": { backgroundColor: theme.palette.primary.lighter },
      "&:focus-visible": { backgroundColor: theme.palette.primary.lighter },
      "&.Mui-disabled": { color: theme.palette.text.disabled },
      "&.Mui-selected": {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.primary.lighter,
        outline: `1px solid ${theme.palette.primary.main}`,
        outlineOffset: -1,
        boxShadow: shadows.input,
      },
      "&.Mui-selected:hover": { backgroundColor: theme.palette.primary.lighter },
      "&.Mui-selected:focus": { backgroundColor: theme.palette.primary.lighter },
    }),
  },
};
